import { Link } from 'react-router-dom';
import {
  TAB_SLUGS,
  type ConversationLocations,
  type ConversationPlace,
  type ConversationPlaceCandidacy,
  type ConversationScriptLocation,
} from '@scoutaround/script-breakdown-shared';

import { CLIENT_SITE_URL } from '../../lib/config.js';
import { locationMetaLine } from '../LocationCard.js';
import { RequirementTag } from '../StatusTag.js';

/**
 * A PLACE OR A SCRIPT LOCATION, SHOWN WHERE IT IS BEING TALKED ABOUT — under a
 * thread about it, and under a `#place` chip in a message.
 *
 * Everything here comes from `GET …/conversations/locations`, which applies the
 * workspace's projection on the server: a listing's photograph, rate and page
 * only while the marketplace lists it, a pin as its derived label and nothing
 * more. This component adds no detail of its own and never reads the body.
 *
 * TWO WAYS OUT. "Show in Locations" goes to the place on this production's
 * Locations tab (`?place=` scrolls to it); "View on ScoutAround" opens the
 * public listing in a new tab through the site's id address `/locations/{id}`,
 * which 301s to the canonical `/film-location/…` URL and survives a renamed
 * listing. Offered only for a listed place — anything else has no public page.
 */

const CANDIDACY_LABEL: Readonly<Record<string, string>> = Object.freeze({
  LONGLIST: 'Suggested',
  SHORTLIST: 'Shortlisted',
  REJECTED: 'Rejected',
  ARCHIVED: 'Archived',
});

function candidacyText(c: ConversationPlaceCandidacy): string {
  const state =
    c.selection === 'PRIMARY'
      ? 'Chosen'
      : c.selection === 'BACKUP'
        ? 'Backup'
        : (CANDIDACY_LABEL[c.status] ?? c.status.toLowerCase());
  return `${state} for ${c.requirementTitle}`;
}

/** The site's id address for a listing; empty when the site's origin is not configured. */
export function publicListingUrl(locationId: number): string | null {
  return CLIENT_SITE_URL === '' ? null : `${CLIENT_SITE_URL}/locations/${String(locationId)}`;
}

export function placeInLocationsPath(projectId: string, locationId: number): string {
  return `/projects/${projectId}/${TAB_SLUGS.LOCATIONS}?place=${String(locationId)}`;
}

/**
 * What a chip says: the place's own short name — a listing's title up to its
 * first comma, at most 40 characters on a word boundary — so a sentence stays
 * a sentence. The full title is on the card and in the chip's tooltip.
 */
export function shortPlaceName(title: string): string {
  const head = (title.split(',')[0] ?? title).trim();
  if (head.length <= 40) return head;
  // One character past the limit, so a word ending exactly at 40 is kept whole.
  const space = head.slice(0, 41).lastIndexOf(' ');
  return `${(space > 20 ? head.slice(0, space) : head.slice(0, 40)).trimEnd()}…`;
}

const LINK_STYLE = { fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' } as const;

export function PlacePreview({
  projectId,
  place,
  testId,
  variant = 'card',
}: {
  projectId: string;
  place: ConversationPlace;
  testId: string;
  /**
   * `card`: framed, with its photograph — under a `#place` chip in a message.
   * `header`: the conversation's own header, which shows the photograph beside
   * the title itself; here only the facts and the links, unframed (2026-11-04).
   */
  variant?: 'card' | 'header';
}) {
  const meta = locationMetaLine(place);
  const publicUrl = place.listed ? publicListingUrl(place.locationId) : null;
  const inHeader = variant === 'header';

  return (
    <div
      data-testid={testId}
      data-variant={variant}
      style={
        inHeader
          ? { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }
          : {
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              padding: 10,
              border: '1px solid var(--color-divider)',
              background: 'var(--color-bg)',
            }
      }
    >
      {place.imageUrl && !inHeader ? (
        // Decorative alt: the name is beside the picture.
        <img
          src={place.imageUrl}
          alt=""
          loading="lazy"
          data-testid={`${testId}-photo`}
          style={{
            width: 132,
            maxWidth: '100%',
            aspectRatio: '10 / 7',
            objectFit: 'cover',
            flex: 'none',
            display: 'block',
            background: 'var(--color-neutral-100)',
          }}
        />
      ) : null}
      <div style={{ flex: 1, minWidth: 180, display: 'grid', gap: 4, alignContent: 'start' }}>
        <strong
          data-testid={`${testId}-title`}
          style={
            inHeader
              ? { fontSize: 13.5, fontWeight: 500, lineHeight: 1.35, color: 'var(--color-neutral-600)', overflowWrap: 'anywhere' }
              : { fontSize: 14, lineHeight: 1.35, overflowWrap: 'anywhere' }
          }
        >
          {place.title}
        </strong>
        {meta !== '' ? (
          <span className="mm" data-testid={`${testId}-meta`} style={{ fontSize: 12.5 }}>
            {meta}
          </span>
        ) : null}
        {place.kind === 'POI' ? (
          <span className="mm" data-testid={`${testId}-pin`} style={{ fontSize: 12 }}>
            A scout&apos;s find. Its photographs and exact position are shared once it is listed.
          </span>
        ) : null}
        {!place.inProduction ? (
          <span data-testid={`${testId}-removed`} style={{ fontSize: 12, color: 'var(--warn)' }}>
            No longer in this production
          </span>
        ) : null}
        {place.candidacies.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {place.candidacies.map((c) => (
              <li
                key={c.requirementId}
                className="chip"
                data-testid={`${testId}-candidacy-${c.requirementId}`}
                style={{ fontSize: 11.5 }}
              >
                {candidacyText(c)}
              </li>
            ))}
          </ul>
        ) : null}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
          {place.inProduction ? (
            <Link
              to={placeInLocationsPath(projectId, place.locationId)}
              data-testid={`${testId}-open`}
              style={LINK_STYLE}
            >
              Show in Locations
            </Link>
          ) : null}
          {publicUrl ? (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-public`}
              style={LINK_STYLE}
            >
              View on ScoutAround ↗
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ScriptLocationPreview({
  projectId,
  scriptLocation: s,
  places,
  testId,
}: {
  projectId: string;
  scriptLocation: ConversationScriptLocation;
  places: ConversationLocations['places'];
  testId: string;
}) {
  const chosen =
    s.primaryLocationId === null ? null : (places?.find((p) => p.locationId === s.primaryLocationId) ?? null);
  const facts = [
    `${String(s.sceneCount)} ${s.sceneCount === 1 ? 'scene' : 'scenes'}`,
    `${String(s.placeCount)} ${s.placeCount === 1 ? 'place' : 'places'} found`,
    s.shortlistedCount > 0 ? `${String(s.shortlistedCount)} shortlisted` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      data-testid={testId}
      style={{ display: 'grid', gap: 4, padding: 10, border: '1px solid var(--color-divider)', background: 'var(--color-bg)' }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong data-testid={`${testId}-title`} style={{ fontSize: 14, overflowWrap: 'anywhere' }}>
          {s.title}
        </strong>
        <RequirementTag status={s.status} />
      </div>
      <span className="mm" data-testid={`${testId}-facts`} style={{ fontSize: 12.5 }}>
        {facts}
      </span>
      {chosen ? (
        <span data-testid={`${testId}-chosen`} style={{ fontSize: 12.5 }}>
          Chosen: <strong>{chosen.title}</strong>
        </span>
      ) : null}
      {s.live && s.placeCount > 0 ? (
        <div style={{ marginTop: 4 }}>
          <Link
            to={`/projects/${projectId}/${TAB_SLUGS.LOCATIONS}/${s.requirementId}`}
            data-testid={`${testId}-compare`}
            style={LINK_STYLE}
          >
            Compare its places
          </Link>
        </div>
      ) : null}
    </div>
  );
}
